import { useState, useEffect } from 'react';
import {
  verifyFeatureAccess,
  verifyReferralMgmtAccess,
  hasFeatureInLocalStorage,
  REFERRAL_REALTIME_UPDATES_FEATURE,
} from '../../../../global/featureFlags';

const parseStoredArray = (key) => {
  try {
    const parsedValue = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const useReferralFeatureFlags = (getReferredStatuses, options = {}) => {
  const {
    skipReferralStatuses = false,
    includeReferralOnlyFlags = true,
  } = options;
  const referralRealtimeEnabled = includeReferralOnlyFlags
    ? hasFeatureInLocalStorage(REFERRAL_REALTIME_UPDATES_FEATURE)
    : false;

  const [flags, setFlags] = useState({
    canForward: false,
    autoUploadToEMR: false,
    createPatientChart: false,
    taskCreation: false,
    hideTaskButton: false,
    sendSmsMessage: false,
    sendMairaCall: false,
    uploadToSFTPFolder: false,
    referralReplyTemplates: [],
    hasReferralManagementAccess: false,
    hasReferralPortal: false,
    referralRealtimeEnabled,
    isLoaded: false,
  });

  useEffect(() => {
    let isMounted = true;

    const loadFlags = async () => {
      const [
        hasReferralManagementAccessResult,
        canForwardResult,
        autoUploadToEMRResult,
        createPatientChartResult,
        taskCreationResult,
        hideTaskButtonResult,
        sendSmsMessageResult,
        sendMairaCallResult,
        // eslint-disable-next-line comma-style
        , // getReferredStatuses() — result unused, side-effect only
        hasReferralPortalResult,
        referralRealtimeEnabledResult,
        uploadToSFTPFolderResult,
      ] = await Promise.allSettled([
        includeReferralOnlyFlags ? verifyReferralMgmtAccess() : Promise.resolve(false),
        includeReferralOnlyFlags ? verifyFeatureAccess('fwd_to_emr') : Promise.resolve(false),
        verifyFeatureAccess('manual_upload_emr'),
        verifyFeatureAccess('create_patient_chart'),
        verifyFeatureAccess('task_creation'),
        verifyFeatureAccess('hide_task_creation_button'),
        includeReferralOnlyFlags ? verifyFeatureAccess('referral_sms') : Promise.resolve(false),
        includeReferralOnlyFlags ? verifyFeatureAccess('referral_maira_call') : Promise.resolve(false),
        // Inbox callers pass { skipReferralStatuses: true } so the inbox doesn't
        // hit /api/v1/referral_statuses. Position is preserved so destructuring
        // indexes below stay stable.
        skipReferralStatuses || !includeReferralOnlyFlags || typeof getReferredStatuses !== 'function'
          ? Promise.resolve()
          : getReferredStatuses(),
        includeReferralOnlyFlags ? verifyFeatureAccess('referral_portal') : Promise.resolve(false),
        includeReferralOnlyFlags ? verifyFeatureAccess(REFERRAL_REALTIME_UPDATES_FEATURE) : Promise.resolve(false),
        verifyFeatureAccess('sftp_upload'),
      ]);

      if (!isMounted) return;

      const resolveBoolean = (result, fallback = false) => (
        result.status === 'fulfilled' ? result.value === true : fallback
      );
      setFlags({
        canForward: resolveBoolean(canForwardResult),
        autoUploadToEMR: resolveBoolean(autoUploadToEMRResult),
        createPatientChart: resolveBoolean(createPatientChartResult),
        taskCreation: resolveBoolean(taskCreationResult),
        hideTaskButton: resolveBoolean(hideTaskButtonResult),
        sendSmsMessage: resolveBoolean(sendSmsMessageResult),
        sendMairaCall: resolveBoolean(sendMairaCallResult),
        referralReplyTemplates: includeReferralOnlyFlags ? parseStoredArray('referral_reply_templates') : [],
        hasReferralManagementAccess: resolveBoolean(hasReferralManagementAccessResult),
        hasReferralPortal: resolveBoolean(hasReferralPortalResult),
        referralRealtimeEnabled: resolveBoolean(referralRealtimeEnabledResult, referralRealtimeEnabled),
        uploadToSFTPFolder: resolveBoolean(uploadToSFTPFolderResult),
        isLoaded: true,
      });
    };

    loadFlags();
    return () => {
      isMounted = false;
    };
  }, [getReferredStatuses, includeReferralOnlyFlags, skipReferralStatuses]);

  return flags;
};

export default useReferralFeatureFlags;
